import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, ArrowLeft, X } from "lucide-react";
import {
  Box,
  Flex,
  Heading,
  Text,
  IconButton,
  Spinner,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Avatar,
  useColorModeValue,
  useToast,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  get_notifications,
  mark_notification_seen,
} from "../../redux/NotificationSlice";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const Notification = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const hasFetched = useRef(false); // ✅ Prevent duplicate fetches

  const { notifications, loading } = useSelector((state) => state.notifications);

  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const newNotificationBg = useColorModeValue("blue.50", "blue.900");
  const newNotificationBorder = useColorModeValue("blue.200", "blue.700");

  // ✅ Fetch notifications ONCE with limit
  useEffect(() => {
    if (hasFetched.current) return; // Prevent duplicate calls
    hasFetched.current = true;

    const fetchNotifications = async () => {
      try {
        await dispatch(get_notifications({ limit: 30 })).unwrap();
      } catch (error) {
        console.error("Error fetching notifications:", error);
        toast({
          title: "Error",
          description: "Failed to load notifications",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };
    fetchNotifications();
  }, [dispatch, toast]);

  const formatDateTime = useCallback((datetime) => {
    const date = new Date(datetime);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day} ${month}, ${hours}:${minutes} ${
      date.getHours() >= 12 ? "PM" : "AM"
    }`;
  }, []);

  const handleNotificationClick = useCallback(
    async (notification) => {
      setDetailsLoading(true);
      setSelectedNotification(notification);

      try {
        // Mark as seen only if unread
        if (notification.seen_status === "0") {
          await dispatch(mark_notification_seen(notification.id)).unwrap();
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load notification details",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        console.error(error);
      } finally {
        setDetailsLoading(false);
      }
    },
    [dispatch, toast]
  );

  if (loading) {
    return (
      <>
      <GasCylinderLoader/>
      </>
    );
  }

  return (
    <Box width="100%" minHeight="100vh" bg={bgColor} overflow="hidden">
      {/* Header */}
      <Flex
        bg="green.600"
        px={4}
        py={3}
        justify="space-between"
        align="center"
        position="sticky"
        top={0}
        zIndex={1}
      >
        <Flex align="center">
          {selectedNotification && (
            <IconButton
              onClick={() => setSelectedNotification(null)}
              mr={3}
              icon={<ArrowLeft size={20} />}
              aria-label="Back"
              variant="ghost"
              color="white"
              _hover={{ bg: "green.700" }}
              size="sm"
            />
          )}
          <Heading as="h2" size={["sm", "md"]} color="white">
            Notifications
          </Heading>
        </Flex>
        <IconButton
          size="sm"
          variant="ghost"
          color="white"
          _hover={{ bg: "green.700" }}
          icon={<X size={16} />}
          aria-label="Close"
        />
      </Flex>

      {/* Main Content */}
      <Box height="calc(100vh - 56px)" overflowY="auto" p={[2, 3]}>
        {/* List View */}
        {!selectedNotification && (
          <>
            {notifications.length === 0 ? (
              <Card bg={bgColor} rounded="lg" boxShadow="sm" textAlign="center">
                <CardBody p={6}>
                  <Text color="gray.500">No notifications available</Text>
                </CardBody>
              </Card>
            ) : (
              <SimpleGrid minChildWidth="xs" spacing="10px">
                {notifications.map((item) => (
                  <Card
                    key={item.id}
                    cursor="pointer"
                    paddingInline={5}
                    paddingBlock={3}
                    transition="all 0.2s ease"
                    _hover={{ boxShadow: "md", transform: "translateY(-3px)" }}
                    borderWidth="1px"
                    rounded="2xl"
                    bg={item.seen_status === "0" ? newNotificationBg : bgColor}
                    borderColor={
                      item.seen_status === "0"
                        ? newNotificationBorder
                        : borderColor
                    }
                    onClick={() => handleNotificationClick(item)}
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                  >
                    {/* Header: Title + Badge */}
                    <Flex justify="space-between" align="center" mb={2}>
                      <Heading
                        as="h3"
                        size="sm"
                        fontWeight={
                          item.seen_status === "0" ? "semibold" : "medium"
                        }
                        color="#007bc9"
                        noOfLines={1}
                      >
                        {item.title || "No Title"}
                      </Heading>

                      {item.seen_status === "0" && (
                        <Badge
                          colorScheme="blue"
                          rounded="full"
                          px={2}
                          fontSize="0.7rem"
                        >
                          New
                        </Badge>
                      )}
                    </Flex>

                    {/* Subject / Body */}
                    <Box flex="1" mb={3}>
                      <Text fontSize="sm" color="gray.700" noOfLines={3}>
                        {item.subject
                          ?.replace(/<[^>]*>/g, "")
                          .substring(0, 100) || "No content available"}
                        {item.subject && item.subject.length > 100 ? "..." : ""}
                      </Text>
                    </Box>

                    {/* Footer: Icon + Date */}
                    <Flex justify="space-between" align="center">
                      <Flex align="center" gap={2}>
                        <Avatar
                          size="xs"
                          bg={
                            item.seen_status === "0" ? "blue.500" : "gray.400"
                          }
                          icon={<Loader2 size={12} color="white" />}
                        />
                        <Text fontSize="xs" color="gray.600">
                          Notification
                        </Text>
                      </Flex>

                      <Text fontSize="xs" color="gray.500">
                        {formatDateTime(item.datetime)}
                      </Text>
                    </Flex>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </>
        )}

        {/* Detail View */}
        {selectedNotification && (
          <Box>
            {detailsLoading ? (
              <>
              <GasCylinderLoader/>
              </>
            ) : (
              <Card
                bg={bgColor}
                rounded="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <CardHeader
                  pb={4}
                  mb={4}
                  borderBottomWidth="1px"
                  borderColor="gray.100"
                >
                  <Heading as="h3" size={["sm", "md"]} color="gray.900" mb={2}>
                    {selectedNotification.title}
                  </Heading>
                  <Flex
                    align="center"
                    gap={2}
                    direction={["column", "row"]}
                    alignItems={["flex-start", "center"]}
                  >
                    <Text fontSize={["xs", "xs"]} color="gray.500">
                      {formatDateTime(selectedNotification.datetime)}
                    </Text>
                    <Badge
                      colorScheme="green"
                      variant="subtle"
                      fontSize="xs"
                      px={2}
                      py={1}
                      rounded="full"
                    >
                      Read
                    </Badge>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <Text
                    fontSize={["sm", "sm"]}
                    color="gray.700"
                    lineHeight="relaxed"
                    dangerouslySetInnerHTML={{
                      __html:
                        selectedNotification.subject?.replace(/\n/g, "<br>") ||
                        "",
                    }}
                  />
                </CardBody>
              </Card>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Notification;